import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { AbstractModel } from '@patsnap/tahiti-core/common/model';
import { type ShareAuthCheckData, type DuplicateModuleData, ShareTaskStatus } from '../../types';
import type { DuplicateService } from '../duplicate-service';
import { DuplicateCreateRepository, DuplicateExecuteRepository } from '../share-tools-repository';
import { RouterService } from '@eureka/shared/core-services/src/vue-router-service';
import { openDuplicatePermissionDialog } from '../../../components/duplicate-permission-dialog/openDuplicatePermissionDialog';

@injectable()
export class DuplicateServiceImpl extends AbstractModel<DuplicateModuleData> implements DuplicateService {
    private taskGroupId = '';
    private _timer: NodeJS.Timeout | null = null;

    _modelData = {
        loading: false,
        status: ShareTaskStatus.PENDING,
    }

    constructor(
        @inject(DuplicateCreateRepository) private duplicateCreateRepository: DuplicateCreateRepository,
        @inject(DuplicateExecuteRepository) private duplicateExecuteRepository: DuplicateExecuteRepository,
        @inject(RouterService) private readonly _routerService: RouterService
    ) {
        super();
    }

    private isNoPermissionError(error: any) {
        return [90170005].includes(error?.data?.error_code || error?.cause?.error_code);
    }

    createShareTask(data: ShareAuthCheckData) {
        this.update((v) => {
            return { ...v, loading: true }
        });
        return this.duplicateCreateRepository.createShareTask(data).then((res) => {
            this.taskGroupId = res;
            return res;
        }).catch(e => {
            this.update((v) => { return { ...v, loading: false, status: ShareTaskStatus.FAILED } });
            if (this.isNoPermissionError(e)) {
                openDuplicatePermissionDialog();
                return ''
            } else {
                throw e;
            }
        });
    }

    openUrl(newDataId: string) {
        const router = this._routerService;
        const currentRoute = router.currentRoute.value;
        const currentParams = { ...currentRoute.params };

        const paramKey = Object.keys(currentParams)[0];
        if (!paramKey) {
            console.warn('No route param found to replace')
            return
        }
        // 更新参数值
        currentParams[paramKey] = newDataId;

        // 如果是在 iframe 中，向 agent 页面发送消息 @ YuDaofang
        const isInIframe = window.self !== window.top;
        if (isInIframe && window.top) {
            return window.top.postMessage({
                type: 'UPDATE_AGENT_PAGE_ID',
                data: {
                    newDataId,
                },
            }, '*');
        }

        // 替换路由，保持原有的 name
        router.replace({
            name: currentRoute.name,
            params: currentParams,
        })
    }

    executeShareTask() {
        if ([ShareTaskStatus.SUCCESS, ShareTaskStatus.FAILED].includes(this.modelData.status)) return
        if (!this.taskGroupId) return
        this.update((v) => {
            return { ...v, loading: true }
        });

        return this.duplicateExecuteRepository.executeShareTask(this.taskGroupId)
            .then(response => {
                if (response) {
                    if (response.status === ShareTaskStatus.PENDING) {
                        this._timer && clearTimeout(this._timer);
                        this._timer = setTimeout(() => this.executeShareTask(), 100);
                    } else {
                        this.update((v) => { return { ...v, status: response.status, loading: false } });
                        this.openUrl(response.new_data_id);
                    }
                }
            })
            .catch(e => {
                this.update((v) => { return { ...v, loading: false, status: ShareTaskStatus.FAILED } });
                throw e;
            })
            .finally(() => {
                this.update((v) => { return { ...v, loading: false } });
            });
    }

    async fetchData(data: ShareAuthCheckData) {
        const id = await this.createShareTask(data)
        if (!id) return;
        return this.executeShareTask();
    }
}
